import { supabase } from "@/lib/supabaseClient";

type ApiClientOptions = Omit<RequestInit, "headers"> & {
	headers?: Record<string, string>;
	requireAuth?: boolean;
};

export type ApiRequestOptions = {
	signal?: AbortSignal;
};

export class ApiError extends Error {
	readonly status: number;
	readonly retryAfterMs?: number;
	readonly requestId?: string;

	constructor(message: string, params: { status: number; retryAfterMs?: number; requestId?: string }) {
		super(message);
		this.name = "ApiError";
		this.status = params.status;
		this.retryAfterMs = params.retryAfterMs;
		this.requestId = params.requestId;
	}
}

type AccessTokenSnapshot = {
	token: string | null;
	expiresAtMs: number | null;
};

let cachedAccessToken: AccessTokenSnapshot = {
	token: null,
	expiresAtMs: null,
};

let accessTokenPromise: Promise<AccessTokenSnapshot> | null = null;

const ACCESS_TOKEN_EARLY_REFRESH_MS = 60_000;

async function refreshAccessToken(): Promise<AccessTokenSnapshot> {
	const { data, error } = await supabase.auth.getSession();
	if (error) {
		cachedAccessToken = { token: null, expiresAtMs: null };
		return cachedAccessToken;
	}

	const session = data.session;
	if (!session) {
		cachedAccessToken = { token: null, expiresAtMs: null };
		return cachedAccessToken;
	}

	const expiresAtMs = session.expires_at ? session.expires_at * 1000 : Date.now() + 30 * 60_000;

	cachedAccessToken = {
		token: session.access_token ?? null,
		expiresAtMs,
	};

	return cachedAccessToken;
}

async function getAccessToken(): Promise<string | null> {
	const now = Date.now();
	const { token, expiresAtMs } = cachedAccessToken;
	if (token && expiresAtMs && expiresAtMs - now > ACCESS_TOKEN_EARLY_REFRESH_MS) {
		return token;
	}

	if (!accessTokenPromise) {
		accessTokenPromise = refreshAccessToken().finally(() => {
			accessTokenPromise = null;
		});
	}

	const next = await accessTokenPromise;
	return next.token;
}

// Keep token cache warm + avoid per-request getSession calls.
supabase.auth.onAuthStateChange((_event, session) => {
	if (!session) {
		cachedAccessToken = { token: null, expiresAtMs: null };
		return;
	}

	cachedAccessToken = {
		token: session.access_token ?? null,
		expiresAtMs: session.expires_at ? session.expires_at * 1000 : null,
	};
});

function parseRetryAfterMs(value: string | null): number | null {
	if (!value) return null;
	const asSeconds = Number(value);
	if (Number.isFinite(asSeconds)) {
		return Math.max(0, Math.floor(asSeconds * 1000));
	}

	const asDate = Date.parse(value);
	if (Number.isFinite(asDate)) {
		return Math.max(0, asDate - Date.now());
	}

	return null;
}

function extractErrorMessage(payload: unknown): string | undefined {
	if (payload && typeof payload === "object" && "message" in payload) {
		const message = (payload as { message?: unknown }).message;
		if (typeof message === "string" && message.trim()) {
			return message;
		}
	}
	return undefined;
}

function resolveApiUrl(path: string): string {
	return path.startsWith("http") ? path : `/api${path.startsWith("/") ? "" : "/"}${path}`;
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
	const needle = name.toLowerCase();
	return Object.keys(headers).some((k) => k.toLowerCase() === needle);
}

function shouldSetJsonContentType(headers: Record<string, string>, body: RequestInit["body"]): boolean {
	const isFormDataBody = typeof FormData !== "undefined" && body instanceof FormData;
	if (isFormDataBody) return false;
	if (hasHeader(headers, "Content-Type")) return false;
	return true;
}

export async function apiFetch(path: string, options: ApiClientOptions = {}): Promise<Response> {
	const url = resolveApiUrl(path);

	const headers: Record<string, string> = {
		...options.headers,
	};

	// Only set default Content-Type if caller didn't specify.
	// Important: for FormData, DO NOT set Content-Type manually (browser adds boundary).
	if (shouldSetJsonContentType(headers, options.body)) {
		headers["Content-Type"] = "application/json";
	}

	const requireAuth = options.requireAuth ?? true;
	if (requireAuth) {
		const token = await getAccessToken();
		if (token) headers.Authorization = `Bearer ${token}`;
	}

	return fetch(url, {
		...options,
		headers,
	});
}

export async function apiJson<T>(path: string, options: ApiClientOptions = {}): Promise<T> {
	const url = resolveApiUrl(path);

	const requireAuth = options.requireAuth ?? true;
	const token = requireAuth ? await getAccessToken() : null;

	const headers: Record<string, string> = {
		...options.headers,
	};
	if (shouldSetJsonContentType(headers, options.body)) {
		headers["Content-Type"] = "application/json";
	}
	if (requireAuth && token) {
		headers.Authorization = `Bearer ${token}`;
	}
	headers["x-request-id"] = headers["x-request-id"] || crypto.randomUUID();

	const response = await fetch(url, {
		...options,
		headers,
	});

	const contentType = response.headers.get("Content-Type") ?? "";
	const canParseJson = contentType.includes("application/json");
	const data: unknown = canParseJson ? await response.json().catch(() => ({})) : {};

	if (!response.ok) {
		const retryAfterMs = parseRetryAfterMs(response.headers.get("Retry-After")) ?? undefined;
		const message = extractErrorMessage(data) || `Request failed: ${response.status}`;
		throw new ApiError(message, { status: response.status, retryAfterMs, requestId: headers["x-request-id"] });
	}

	if (data && typeof data === "object") {
		const maybeResponseObject = (data as { responseObject?: unknown }).responseObject;
		if (maybeResponseObject !== undefined) return maybeResponseObject as T;
		const maybeData = (data as { data?: unknown }).data;
		if (maybeData !== undefined) return maybeData as T;
	}

	return data as T;
}
